/**
 * フォームビルダーのビジネスロジック
 * UI状態 ⇄ JSON Schema の変換を行う
 */

import type { BuilderState, FormField } from '../types/formBuilder'

/**
 * BuilderStateからform_schema（JSON Schema）を生成
 */
export function generateFormSchema(state: BuilderState): string {
  const properties: Record<string, any> = {}
  const required: string[] = []

  for (const field of state.fields) {
    const prop: any = {
      title: field.label,
    }

    if (field.description) {
      prop.description = field.description
    }

    switch (field.type) {
      case 'text':
      case 'textarea':
        prop.type = 'string'
        if (field.minLength !== undefined) {
          prop.minLength = field.minLength
        }
        if (field.maxLength !== undefined) {
          prop.maxLength = field.maxLength
        }
        break

      case 'number':
        prop.type = 'number'
        if (field.minimum !== undefined) {
          prop.minimum = field.minimum
        }
        if (field.maximum !== undefined) {
          prop.maximum = field.maximum
        }
        break

      case 'date':
        prop.type = 'string'
        prop.format = 'date'
        break

      case 'checkbox':
        prop.type = 'boolean'
        break

      case 'select':
        prop.type = 'string'
        if (field.options && field.options.length > 0) {
          prop.enum = field.options.map(opt => opt.value)
          prop.enumNames = field.options.map(opt => opt.label)
        }
        break

      case 'time':
        prop.type = 'string'
        prop.format = 'time'
        if (field.defaultValue) {
          prop.default = field.defaultValue
        }
        if (field.minTime) {
          prop['x-timeMinimum'] = field.minTime
        }
        if (field.maxTime) {
          prop['x-timeMaximum'] = field.maxTime
        }
        break

      case 'datetime':
        prop.type = 'string'
        prop.format = 'date-time'
        if (field.defaultValue) {
          prop.default = field.defaultValue
        }
        if (field.minDateTime) {
          prop.formatMinimum = field.minDateTime
        }
        if (field.maxDateTime) {
          prop.formatMaximum = field.maxDateTime
        }
        break

      case 'radio':
        prop.type = 'string'
        if (field.options && field.options.length > 0) {
          prop.enum = field.options.map(opt => opt.value)
          prop.enumNames = field.options.map(opt => opt.label)
        }
        if (field.defaultValue) {
          prop.default = field.defaultValue
        }
        break

      case 'multiselect':
        prop.type = 'array'
        prop.items = {
          type: 'string',
          ...(field.options && field.options.length > 0 ? {
            enum: field.options.map(opt => opt.value),
            enumNames: field.options.map(opt => opt.label)
          } : {})
        }
        prop.uniqueItems = true
        if (field.defaultValue && field.defaultValue.length > 0) {
          prop.default = field.defaultValue
        }
        break
    }

    properties[field.name] = prop

    if (field.required) {
      required.push(field.name)
    }
  }

  const schema = {
    type: 'object',
    properties,
    ...(required.length > 0 ? { required } : {}),
  }

  return JSON.stringify(schema, null, 2)
}

/**
 * BuilderStateからui_schemaを生成
 */
export function generateUiSchema(state: BuilderState): string {
  const uiSchema: Record<string, any> = {}

  for (const field of state.fields) {
    const uiOptions: any = {}

    switch (field.type) {
      case 'text':
        if (field.placeholder) {
          uiOptions['ui:placeholder'] = field.placeholder
        }
        break

      case 'textarea':
        uiOptions['ui:widget'] = 'textarea'
        if (field.placeholder) {
          uiOptions['ui:placeholder'] = field.placeholder
        }
        break

      case 'number':
        if (field.placeholder) {
          uiOptions['ui:placeholder'] = field.placeholder
        }
        break

      case 'date':
        uiOptions['ui:widget'] = 'date'
        break

      case 'checkbox':
        uiOptions['ui:widget'] = 'checkbox'
        break

      case 'select':
        // selectは特に ui:widget の指定不要（デフォルトでドロップダウン）
        break

      case 'time':
        uiOptions['ui:widget'] = 'time'
        if (field.stepMinutes) {
          uiOptions['ui:options'] = {
            step: field.stepMinutes * 60 // 秒に変換
          }
        }
        break

      case 'datetime':
        uiOptions['ui:widget'] = 'datetime'
        break

      case 'radio':
        uiOptions['ui:widget'] = 'radio'
        break

      case 'multiselect':
        uiOptions['ui:widget'] = 'checkboxes'
        break
    }

    if (Object.keys(uiOptions).length > 0) {
      uiSchema[field.name] = uiOptions
    }
  }

  // ui:orderでフィールドの順序を指定
  uiSchema['ui:order'] = state.fields.map(f => f.name)

  return JSON.stringify(uiSchema, null, 2)
}

/**
 * BuilderStateからvalidation_configを生成
 * MVP版では空オブジェクトを返す
 */
export function generateValidationConfig(_state: BuilderState): string {
  return JSON.stringify({}, null, 2)
}

/**
 * JSON SchemaからBuilderStateへ逆変換（基本型のみ対応）
 */
export function parseFormSchema(formSchemaStr: string): BuilderState {
  try {
    const schema = JSON.parse(formSchemaStr)
    const fields: FormField[] = []

    if (schema.type !== 'object' || !schema.properties) {
      return { fields: [] }
    }

    const properties = schema.properties
    const required: string[] = schema.required || []

    for (const [name, prop] of Object.entries(properties)) {
      const propDef: any = prop
      const baseField = {
        id: generateFieldId(),
        name,
        label: propDef.title || name,
        description: propDef.description,
        required: required.includes(name),
      }

      // 型判定
      if (propDef.type === 'boolean') {
        fields.push({
          ...baseField,
          type: 'checkbox',
        })
      } else if (propDef.type === 'number') {
        fields.push({
          ...baseField,
          type: 'number',
          minimum: propDef.minimum,
          maximum: propDef.maximum,
        })
      } else if (propDef.type === 'string') {
        if (propDef.format === 'date') {
          fields.push({
            ...baseField,
            type: 'date',
          })
        } else if (propDef.format === 'time') {
          fields.push({
            ...baseField,
            type: 'time',
            defaultValue: propDef.default,
            minTime: propDef['x-timeMinimum'],
            maxTime: propDef['x-timeMaximum'],
          })
        } else if (propDef.format === 'date-time') {
          fields.push({
            ...baseField,
            type: 'datetime',
            defaultValue: propDef.default,
            minDateTime: propDef.formatMinimum,
            maxDateTime: propDef.formatMaximum,
          })
        } else if (propDef.enum && Array.isArray(propDef.enum)) {
          const enumNames = propDef.enumNames || propDef.enum
          // enumはデフォルトでselectとして扱う（radioはui:widgetで判断が必要だが、ここでは区別できない）
          fields.push({
            ...baseField,
            type: 'select',
            options: propDef.enum.map((val: string, idx: number) => ({
              value: val,
              label: enumNames[idx] || val,
            })),
          })
        } else {
          // text or textarea の判定（ui:widgetを見ないと分からないので、とりあえずtext）
          fields.push({
            ...baseField,
            type: 'text',
            minLength: propDef.minLength,
            maxLength: propDef.maxLength,
          })
        }
      } else if (propDef.type === 'array') {
        // multiselectの判定
        if (propDef.items && propDef.items.type === 'string' && propDef.items.enum) {
          const enumNames = propDef.items.enumNames || propDef.items.enum
          fields.push({
            ...baseField,
            type: 'multiselect',
            options: propDef.items.enum.map((val: string, idx: number) => ({
              value: val,
              label: enumNames[idx] || val,
            })),
            defaultValue: propDef.default,
          })
        }
      }
    }

    return { fields }
  } catch (error) {
    console.error('Failed to parse form schema:', error)
    return { fields: [] }
  }
}

/**
 * 一意なフィールドIDを生成
 */
export function generateFieldId(): string {
  return `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}

/**
 * フィールド名のバリデーション
 */
export function validateFieldName(name: string, existingNames: string[], currentFieldId?: string): string | null {
  // 空チェック
  if (!name || name.trim() === '') {
    return 'フィールド名を入力してください'
  }

  // フォーマットチェック（[a-zA-Z_][a-zA-Z0-9_]*）
  if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(name)) {
    return 'フィールド名は英字またはアンダースコアで始まり、英数字とアンダースコアのみ使用できます'
  }

  // 重複チェック（自分自身は除外）
  const otherNames = existingNames.filter((_, idx) => {
    // currentFieldIdが指定されている場合、それと一致するフィールドは除外
    return true // 簡易実装：全体で重複チェック
  })

  if (otherNames.includes(name)) {
    return 'このフィールド名は既に使用されています'
  }

  return null
}

/**
 * BuilderStateのバリデーション
 */
export function validateBuilderState(state: BuilderState): string[] {
  const errors: string[] = []
  const names = state.fields.map(f => f.name)

  // フィールド名の重複チェック
  const nameSet = new Set<string>()
  for (const field of state.fields) {
    if (nameSet.has(field.name)) {
      errors.push(`フィールド名 "${field.name}" が重複しています`)
    }
    nameSet.add(field.name)

    // 個別のフィールド名バリデーション
    const nameError = validateFieldName(field.name, names.filter(n => n !== field.name))
    if (nameError) {
      errors.push(`フィールド "${field.label}": ${nameError}`)
    }

    // selectフィールドのoptionsチェック
    if (field.type === 'select' && (!field.options || field.options.length === 0)) {
      errors.push(`選択肢フィールド "${field.label}" には最低1つの選択肢が必要です`)
    }
  }

  return errors
}
